import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth, hasRole } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Plus, GitCommitHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export default function DeploymentsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = hasRole(user, ['admin', 'tech']);
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ service_id: '', version: '', notes: '', status: 'success' });

  const load = async () => {
    const [{ data }, { data: svcs }] = await Promise.all([api.get('/deployments'), api.get('/services')]);
    setItems(data); setServices(svcs);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post('/deployments', form); toast.success(t('success')); setOpen(false); setForm({ service_id: '', version: '', notes: '', status: 'success' }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
  };

  const grouped = items.reduce((acc, d) => { (acc[d.service_name] = acc[d.service_name] || []).push(d); return acc; }, {});

  return (
    <div className="space-y-6" data-testid="deployments-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">{t('deployments_title')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{items.length} total</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="deployments-create-button"><Plus className="h-4 w-4 mr-2" />{t('deployments_new')}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t('deployments_new')}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1"><Label>{t('logs_service')}</Label>
                  <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                    <SelectTrigger data-testid="deployment-form-service"><SelectValue /></SelectTrigger>
                    <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Version</Label><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="deployment-form-version" /></div>
                <div className="space-y-1"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div className="space-y-1"><Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="success">success</SelectItem>
                      <SelectItem value="failed">failed</SelectItem>
                      <SelectItem value="running">running</SelectItem>
                      <SelectItem value="rolled_back">rolled_back</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>{t('cancel')}</Button><Button onClick={create} data-testid="deployment-form-submit">{t('create')}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-4" data-testid="deployments-table">
        {Object.keys(grouped).map((svcName) => (
          <Card key={svcName} className="p-5 bg-card border border-border">
            <h2 className="font-display text-lg font-semibold mb-3">{svcName}</h2>
            <ol className="relative border-l border-border ml-2">
              {grouped[svcName].map((d) => (
                <li key={d.id} className="ml-5 mb-4" data-testid={`deployment-item-${d.id}`}>
                  <span className={`absolute -left-1.5 mt-1 h-3 w-3 rounded-full border-2 border-background ${d.status === 'success' ? 'bg-emerald-400' : d.status === 'running' ? 'bg-[hsl(var(--primary))]' : 'bg-red-400'}`} />
                  <div className="flex items-center gap-2">
                    <span className="font-display text-base font-semibold">v{d.version}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] uppercase">{d.status}</span>
                    <span className="font-mono text-xs text-[hsl(var(--muted-foreground))]"><GitCommitHorizontal className="inline h-3 w-3" /> {d.commit_hash}</span>
                  </div>
                  <div className="text-sm text-[hsl(var(--muted-foreground))]">{d.notes}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{d.author} • {d.created_at ? formatDistanceToNow(new Date(d.created_at), { addSuffix: true }) : ''}</div>
                </li>
              ))}
            </ol>
          </Card>
        ))}
        {items.length === 0 && <Card className="p-8 text-center text-[hsl(var(--muted-foreground))] bg-card border border-border">{t('no_data')}</Card>}
      </div>
    </div>
  );
}
